import { defineComponent as _defineComponent } from 'vue'
import * as echarts from 'echarts'
import { inject, onMounted, onUnmounted, ref } from 'vue'
import { withRefLoading } from '@/utils/task'
import type { EChartImpl } from './EChartImpl'


export default /*#__PURE__*/_defineComponent({
  __name: 'EChart',
  props: {
    requestFn: null,
    translateBg: { type: Boolean }
  },
  emits: ["keyup", "resizedChart"],
  setup(__props: any, { expose, emit }: { emit: ({
    (e: 'keyup', ev: KeyboardEvent): void
    (e: 'resizedChart'): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    requestFn?: (chart: echarts.ECharts) => Promise<void>
    translateBg?: boolean
}





const registerChildECharts = inject<(chart: EChartImpl) => (() => void)>('registerChildECharts', () => () => { })

const wrapperRef = ref<HTMLDivElement>(null)
const chartRef = ref<HTMLDivElement>(null)
const chart = ref<echarts.ECharts>(null)
const loading = ref(false)
const hasRequest = ref(false)

let unregisterChildECharts: () => void
let resizeObserver: ResizeObserver
let unlistenWindowResize: () => void
let resizeFrame = 0
let latestSize = {
    width: 0,
    height: 0,
}

function request() {
    hasRequest.value = true
    if (!props.requestFn || !chart.value) {
        return Promise.resolve()
    }
    return withRefLoading(loading, Promise.resolve(props.requestFn(chart.value)))
}

function reset() {
    if (chart.value) {
        chart.value.setOption({}, true)
    }
}

function getWrapperSize() {
    if (!wrapperRef.value) {
        return null
    }
    const rect = wrapperRef.value.getBoundingClientRect()
    const width = Math.floor(rect.width)
    const height = Math.floor(rect.height)
    if (width <= 0 || height <= 0) {
        return null
    }
    return {
        width,
        height,
    }
}

function syncSize() {
    const size = getWrapperSize()
    if (!size || !chartRef.value) {
        return false
    }

    if (latestSize.width !== size.width) {
        chartRef.value.style.width = size.width + 'px'
    }
    if (latestSize.height !== size.height) {
        chartRef.value.style.height = size.height + 'px'
    }
    latestSize = size
    return true
}

function resizeChart() {
    if (syncSize() && chart.value) {
        chart.value.resize(latestSize)
    }
    emit('resizedChart')
}

function scheduleResizeChart() {
    if (resizeFrame) {
        cancelAnimationFrame(resizeFrame)
    }
    resizeFrame = requestAnimationFrame(() => {
        resizeFrame = 0
        resizeChart()
    })
}

function onKeyUp(ev: KeyboardEvent) {
    emit('keyup', ev)
}

function clearData() {
    if (chart.value) {
        chart.value.clear()
    }
}

const exposedChart = {
    request,
    reset,
    syncSize,
    resizeChart,
    onKeyUp,
    clearData,
    get chart() {
        return chart.value
    },
    get loading() {
        return loading.value
    },
    get hasRequest() {
        return hasRequest.value
    },
} as EChartImpl

onMounted(() => {
    chart.value = echarts.init(chartRef.value)
    unregisterChildECharts = registerChildECharts(exposedChart)

    if (window.ResizeObserver) {
        resizeObserver = new ResizeObserver(() => {
            scheduleResizeChart()
        })
        resizeObserver.observe(wrapperRef.value)
    } else {
        window.addEventListener('resize', scheduleResizeChart)
        unlistenWindowResize = () => {
            window.removeEventListener('resize', scheduleResizeChart)
        }
    }
    scheduleResizeChart()
})

onUnmounted(() => {
    if (resizeFrame) {
        cancelAnimationFrame(resizeFrame)
        resizeFrame = 0
    }
    if (resizeObserver) {
        resizeObserver.disconnect()
    }
    if (unlistenWindowResize) {
        unlistenWindowResize()
    }
    if (unregisterChildECharts) {
        unregisterChildECharts()
    }
    if (chart.value) {
        chart.value.dispose()
        chart.value = null
    }
})

expose(exposedChart)

return { __sfc: true,props, emit, registerChildECharts, wrapperRef, chartRef, chart, loading, hasRequest, unregisterChildECharts, resizeObserver, unlistenWindowResize, resizeFrame, latestSize, request, reset, getWrapperSize, syncSize, resizeChart, scheduleResizeChart, onKeyUp, clearData, exposedChart }
}

})